import React, { useState } from 'react';
import { Send, Smartphone, Mail, CheckCircle, User } from 'lucide-react';

/**
 * SendInvitation — two modes in one modal:
 *
 * 1. Confirm mode (default) — used by the "Add Student" flow (StudentList.jsx).
 *    The registration link is always sent to the phone number already on the
 *    student's record (see backend sendRegistrationForStudent, which reads
 *    student.phone directly — it does not accept a contact override). So this
 *    mode shows the student's name + phone for confirmation rather than
 *    collecting a contact method — there is nothing to choose or type.
 *    To change the number a link goes to, edit the student's profile first
 *    (Edit Student), then use this button again.
 *
 * 2. Capture mode (`captureContact`) — used by the "Add Instructor" flow
 *    (Instructors.jsx, Admin-only). Neither contact is known yet, so this
 *    mode collects BOTH email and phone (both mandatory) and lets the admin
 *    pick which channel actually delivers the invitation link — the other
 *    contact is still stored on the invitation for reference / a future
 *    resend via the other channel.
 */
const SendInvitation = ({
  studentName, phone, onClose, onSend,
  captureContact = false, entityLabel = 'Student'
}) => {
  const [error, setError]                   = useState('');
  const [isSending, setIsSending]           = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ── Capture-mode state (email + phone + channel choice) ──────────────────
  const [captureEmail, setCaptureEmail] = useState('');
  const [capturePhone, setCapturePhone] = useState('');
  const [sendMethod,   setSendMethod]   = useState('email'); // 'email' | 'sms'

  const hasPhone = !!phone;

  const validateCapture = () => {
    if (!captureEmail.trim()) {
      setError('Email address is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(captureEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!capturePhone.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (capturePhone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    if (captureContact) {
      if (!validateCapture()) return;
    } else if (!hasPhone) {
      setError('No phone number on file — add one via Edit Student first.');
      return;
    }

    setIsSending(true);
    setError('');
    setSuccessMessage('');

    try {
      if (captureContact) {
        await onSend({
          type:      sendMethod,
          email:     captureEmail.trim(),
          phone:     capturePhone.trim(),
          timestamp: new Date().toISOString()
        });

        setSuccessMessage(
          sendMethod === 'sms'
            ? `Invitation text sent to ${capturePhone.trim()}`
            : `Invitation email sent to ${captureEmail.trim()}`
        );
      } else {
        await onSend({
          type:      'sms',
          contact:   phone,
          timestamp: new Date().toISOString()
        });

        setSuccessMessage(`Registration text sent to ${studentName || 'student'} (${phone})`);
      }

      setIsSending(false);
      setTimeout(() => { onClose(); }, 3000);

    } catch (err) {
      setIsSending(false);
      console.error('SendInvitation error:', err);

      const errorMessage = err.message || err.error || 'Unknown error occurred';

      if (
        errorMessage.toLowerCase().includes('already been sent') ||
        errorMessage.toLowerCase().includes('already exists')   ||
        errorMessage.toLowerCase().includes('invalid')          ||
        errorMessage.toLowerCase().includes('not found')        ||
        errorMessage.toLowerCase().includes('required')
      ) {
        setError(errorMessage);
      }
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="template-modal-overlay">
      <div className="template-modal">
        <div className="modal-header">
          <h3>{captureContact ? `Invite ${entityLabel}` : 'Send Registration SMS'}</h3>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={isSending || !!successMessage}
          >
            ×
          </button>
        </div>

        <div className="template-list">

          {/* ── Success ── */}
          {successMessage && (
            <div style={{
              padding:         '1rem',
              backgroundColor: '#d1fae5',
              border:          '1px solid #10b981',
              borderRadius:    '0.5rem',
              marginBottom:    '1rem',
              display:         'flex',
              alignItems:      'center',
              gap:             '0.75rem'
            }}>
              <CheckCircle size={24} color="#10b981" />
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, color: '#065f46', fontWeight: '500', fontSize: '0.9375rem' }}>
                  {successMessage}
                </p>
                <p style={{ margin: '0.25rem 0 0 0', color: '#059669', fontSize: '0.8125rem' }}>
                  This window will close automatically…
                </p>
              </div>
            </div>
          )}

          {/* ── Capture mode — collect email + phone, pick delivery channel ── */}
          {!successMessage && captureContact && (
            <>
              <div className="form-group" style={{ marginBottom: '0.9rem' }}>
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  value={captureEmail}
                  onChange={(e) => { setCaptureEmail(e.target.value); setError(''); }}
                  placeholder="john@example.com"
                  disabled={isSending}
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  value={capturePhone}
                  onChange={(e) => { setCapturePhone(e.target.value); setError(''); }}
                  placeholder="+44 7700 900000"
                  disabled={isSending}
                  className="form-input"
                />
                <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.78rem', color: '#6b7280' }}>
                  Include country code, e.g. +44 for UK
                </span>
              </div>

              <div className="form-group" style={{ marginBottom: '1.1rem' }}>
                <label className="form-label">Send invitation via</label>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem' }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1,
                    padding: '0.65rem 0.8rem', border: '2px solid',
                    borderColor: sendMethod === 'email' ? '#2563eb' : '#e5e7eb',
                    borderRadius: '0.5rem', cursor: 'pointer',
                    background: sendMethod === 'email' ? '#eff6ff' : 'white'
                  }}>
                    <input type="radio" name="sendMethod" value="email"
                      checked={sendMethod === 'email'}
                      onChange={() => setSendMethod('email')}
                      style={{ accentColor: '#2563eb', cursor: 'pointer' }} />
                    <Mail size={16} color="#2563eb" />
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>Email</span>
                  </label>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1,
                    padding: '0.65rem 0.8rem', border: '2px solid',
                    borderColor: sendMethod === 'sms' ? '#7c3aed' : '#e5e7eb',
                    borderRadius: '0.5rem', cursor: 'pointer',
                    background: sendMethod === 'sms' ? '#f5f3ff' : 'white'
                  }}>
                    <input type="radio" name="sendMethod" value="sms"
                      checked={sendMethod === 'sms'}
                      onChange={() => setSendMethod('sms')}
                      style={{ accentColor: '#7c3aed', cursor: 'pointer' }} />
                    <Smartphone size={16} color="#7c3aed" />
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>SMS</span>
                  </label>
                </div>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: '#9ca3af' }}>
                  Both email and phone are saved either way — this just picks which one the link is sent to now.
                </p>
              </div>

              {error && (
                <div style={{
                  background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.5rem',
                  padding: '0.65rem 0.8rem', color: '#991b1b', fontSize: '0.875rem', marginBottom: '1rem'
                }}>
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={onClose} disabled={isSending} className="btn-cancel">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending}
                  className="btn-save"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Send size={18} />
                  {isSending ? 'Sending…' : 'Send Invitation'}
                </button>
              </div>
            </>
          )}

          {/* ── Confirmation mode — contact already known, nothing to type ── */}
          {!successMessage && !captureContact && (
            <>
              <div style={{
                display:         'flex',
                alignItems:      'center',
                gap:             '0.85rem',
                padding:         '0.9rem 1rem',
                border:          '1.5px solid #e5e7eb',
                borderRadius:    '0.5rem',
                marginBottom:    '1.25rem',
                background:      '#f9fafb'
              }}>
                <div style={{
                  width: '2.5rem', height: '2.5rem', borderRadius: '50%',
                  background: '#eef2ff', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <User size={20} color="#4f46e5" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
                    {studentName || 'Student'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.15rem' }}>
                    <Smartphone size={14} color="#6b7280" />
                    <span style={{ fontSize: '0.85rem', color: hasPhone ? '#374151' : '#ef4444' }}>
                      {hasPhone ? phone : 'No phone number on file'}
                    </span>
                  </div>
                </div>
              </div>

              <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
                {hasPhone
                  ? 'A one-time registration link will be sent by text message to this number.'
                  : 'Add a phone number to this student\u2019s profile before sending a registration link.'}
              </p>

              {error && (
                <div style={{
                  background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.5rem',
                  padding: '0.65rem 0.8rem', color: '#991b1b', fontSize: '0.875rem', marginBottom: '1rem'
                }}>
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="form-actions">
                <button type="button" onClick={onClose} disabled={isSending} className="btn-cancel">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending || !hasPhone}
                  className="btn-save"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Send size={18} />
                  {isSending ? 'Sending…' : 'Send Registration SMS'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendInvitation;